import { isTimelineDays, RESERVATION_STATUSES, type ReservationStatus } from '@rental/domain'
import { createRootRoute, createRoute, createRouter, type RouteIds } from '@tanstack/react-router'
import { isIsoDate } from './board'
import { BookingScreen } from './screens/Booking'
import { BookingsScreen } from './screens/Bookings'
import { CashScreen } from './screens/Cash'
import { ChangePasswordScreen } from './screens/ChangePassword'
import { CheckoutScreen } from './screens/Checkout'
import { CrashScreen } from './screens/Crash'
import { CustomerScreen } from './screens/Customer'
import { CustomersScreen } from './screens/Customers'
import { DevicesScreen } from './screens/Devices'
import { FallbackKitScreen } from './screens/FallbackKit'
import { FineScreen } from './screens/Fine'
import { FinesScreen } from './screens/Fines'
import { FiscalScreen } from './screens/Fiscal'
import { MaintenanceScreen } from './screens/Maintenance'
import { ModelsScreen } from './screens/Models'
import { NewBookingScreen } from './screens/NewBooking'
import { NewPersonScreen } from './screens/NewPerson'
import { NotFoundScreen } from './screens/NotFound'
import { PaperScreen } from './screens/Paper'
import { PeopleScreen } from './screens/People'
import { PersonScreen } from './screens/Person'
import { PricesScreen } from './screens/Prices'
import { ReconciliationScreen } from './screens/Reconciliation'
import { ReportsScreen } from './screens/Reports'
import { ReturnScreen } from './screens/Return'
import { Root } from './screens/Root'
import { SetPinScreen } from './screens/SetPin'
import { SystemScreen } from './screens/System'
import { TermsScreen } from './screens/Terms'
import { TimelineScreen } from './screens/Timeline'
import { TodayScreen } from './screens/Today'
import { VehicleScreen } from './screens/Vehicle'
import { VehiclesScreen } from './screens/Vehicles'
import { reportError } from './telemetry'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface TodaySearch {
  date?: string | undefined
  location?: string | undefined
}

/** A booking just made, handed over, taken back or typed in from paper, says so once. */
export interface BookingSearch {
  created?: true | undefined
  done?: 'checkout' | 'return' | 'paper' | undefined
}

export interface BookingsSearch {
  q?: string | undefined
  status?: ReservationStatus | undefined
  from?: string | undefined
  to?: string | undefined
  page?: number | undefined
}

/** URL search parameters are typed by hand: anything odd is dropped, never trusted. */
function todaySearch(search: Record<string, unknown>): TodaySearch {
  return {
    ...(isIsoDate(search.date) ? { date: search.date } : {}),
    ...(typeof search.location === 'string' && UUID.test(search.location)
      ? { location: search.location }
      : {}),
  }
}

function bookingsSearch(search: Record<string, unknown>): BookingsSearch {
  const page = Number(search.page)
  return {
    ...(typeof search.q === 'string' && search.q.trim()
      ? { q: search.q.trim().slice(0, 100) }
      : {}),
    ...((RESERVATION_STATUSES as readonly unknown[]).includes(search.status)
      ? { status: search.status as ReservationStatus }
      : {}),
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
    ...(Number.isInteger(page) && page > 1 && page <= 1000 ? { page } : {}),
  }
}

export interface NewBookingSearch {
  customer?: string | undefined
  /** Pre-fill from a Timeline drag: the category, vehicle and Rome wall-clock range. */
  category?: string | undefined
  vehicle?: string | undefined
  pickupDate?: string | undefined
  pickupTime?: string | undefined
  returnDate?: string | undefined
  returnTime?: string | undefined
}

export interface TimelineSearch {
  from?: string | undefined
  /** How many days from it, 1 to TIMELINE_MAX_DAYS (OPS-1); the device's last choice, else 7, when none. */
  days?: number | undefined
  category?: string | undefined
}

/** W2-S S1: Prezzi's days, as the Planning's: from a day, how many, one category or all. */
export interface PricesSearch {
  from?: string | undefined
  /** 1 to TIMELINE_MAX_DAYS, as the API takes; the device's last choice, else 14, when none. */
  days?: number | undefined
  category?: string | undefined
}

export interface VehiclesSearch {
  category?: string | undefined
  q?: string | undefined
  retired?: true | undefined
}

export interface CustomersSearch {
  q?: string | undefined
  page?: number | undefined
}

/** S5c: the money reports: Ricavi (the default) or Aperti, and Ricavi's period. */
export interface ReportsSearch {
  tab?: 'open' | undefined
  from?: string | undefined
  to?: string | undefined
  by?: 'month' | undefined
}

/** UI-2e: Fiscale's period, in the address so Back and a reload keep it; the last thirty days when none is chosen. */
export interface FiscalSearch {
  from?: string | undefined
  to?: string | undefined
}

/** S6 (CMP-2): the fines register: the open ones (the default) or all, and a search. */
export interface FinesSearch {
  view?: 'all' | undefined
  q?: string | undefined
}

/** A notice just registered says so once on its own page. */
export interface FineSearch {
  done?: 'registered' | undefined
}

/** S5c: the day of the check against Stripe; yesterday when none is chosen. */
export interface ReconciliationSearch {
  date?: string | undefined
}

const uuidParam = (value: unknown) =>
  typeof value === 'string' && UUID.test(value) ? value : undefined
const timeParam = (value: unknown) =>
  typeof value === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? value : undefined
const textParam = (value: unknown, max: number) =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined
const pageParam = (value: unknown) => {
  const page = Number(value)
  return Number.isInteger(page) && page > 1 && page <= 1000 ? page : undefined
}

/** Drops the keys that are not set, so the URL carries only what was chosen. */
function only<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T
}

const rootRoute = createRootRoute({
  component: Root,
  notFoundComponent: NotFoundScreen,
})

// Each route is a constant of its own: created inline in addChildren, a route loses its
// path's literal type, and with it every typed search and param (UI-2e). The check
// under the tree keeps it so.
const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: todaySearch,
  component: TodayScreen,
})

const bookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings',
  validateSearch: bookingsSearch,
  component: BookingsScreen,
})

const newBookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/new',
  validateSearch: (search: Record<string, unknown>): NewBookingSearch =>
    only({
      customer: uuidParam(search.customer),
      category: uuidParam(search.category),
      vehicle: uuidParam(search.vehicle),
      pickupDate: isIsoDate(search.pickupDate) ? search.pickupDate : undefined,
      pickupTime: timeParam(search.pickupTime),
      returnDate: isIsoDate(search.returnDate) ? search.returnDate : undefined,
      returnTime: timeParam(search.returnTime),
    }),
  component: NewBookingScreen,
})

const bookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$code',
  validateSearch: (search: Record<string, unknown>): BookingSearch =>
    only({
      created: search.created === true || search.created === 'true' ? true : undefined,
      done:
        search.done === 'checkout' || search.done === 'return' || search.done === 'paper'
          ? search.done
          : undefined,
    }),
  component: BookingScreen,
})

const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$code/checkout',
  component: CheckoutScreen,
})

const returnRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$code/return',
  component: ReturnScreen,
})

/** S11: a rental kept on paper, typed back in by the Owner. */
const paperRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/bookings/$code/paper',
  component: PaperScreen,
})

const timelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/timeline',
  validateSearch: (search: Record<string, unknown>): TimelineSearch => {
    const days = Number(search.days)
    // Every key named, set or not (OPS-1): the router lays the parent's raw search under a
    // child's validated one (`preMatchSearch = { ...parentSearch, ...strictSearch }`), so a
    // key left out comes back with the address's raw value: `?days=93` reached the board
    // and the API refused it. An `undefined` here overrides it, and the address never
    // carries it (the router's encoder drops undefined values).
    return {
      from: isIsoDate(search.from) ? search.from : undefined,
      days: isTimelineDays(days) ? days : undefined,
      category: uuidParam(search.category),
    }
  },
  component: TimelineScreen,
})

const pricesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prezzi',
  // Every key named, set or not, as the Planning's (timelineRoute): a key left out would
  // come back with the address's raw value.
  validateSearch: (search: Record<string, unknown>): PricesSearch => {
    const days = Number(search.days)
    return {
      from: isIsoDate(search.from) ? search.from : undefined,
      days: isTimelineDays(days) ? days : undefined,
      category: uuidParam(search.category),
    }
  },
  component: PricesScreen,
})

const vehiclesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vehicles',
  validateSearch: (search: Record<string, unknown>): VehiclesSearch =>
    only({
      category: uuidParam(search.category),
      q: textParam(search.q, 40),
      retired: search.retired === true || search.retired === 'true' ? true : undefined,
    }),
  component: VehiclesScreen,
})

// Q-157: the models standing for each category, for whoever holds models.manage.
const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/modelli',
  component: ModelsScreen,
})

const vehicleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vehicles/$vehicleId',
  component: VehicleScreen,
})

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers',
  validateSearch: (search: Record<string, unknown>): CustomersSearch =>
    only({ q: textParam(search.q, 100), page: pageParam(search.page) }),
  component: CustomersScreen,
})

const customerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$customerId',
  component: CustomerScreen,
})

// Manutenzione: service and repair interventions with their cost (managers).
const maintenanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/maintenance',
  component: MaintenanceScreen,
})

// S5 (PAY-6): the cash drawers.
const cashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cash',
  component: CashScreen,
})

const fiscalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fiscal',
  validateSearch: (search: Record<string, unknown>): FiscalSearch =>
    only({
      from: isIsoDate(search.from) ? search.from : undefined,
      to: isIsoDate(search.to) ? search.to : undefined,
    }),
  component: FiscalScreen,
})

// S6 (CMP-2): the fines register, and one fine.
const finesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fines',
  validateSearch: (search: Record<string, unknown>): FinesSearch =>
    only({
      view: search.view === 'all' ? 'all' : undefined,
      q: textParam(search.q, 60),
    }),
  component: FinesScreen,
})

const fineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fines/$fineId',
  validateSearch: (search: Record<string, unknown>): FineSearch =>
    only({ done: search.done === 'registered' ? 'registered' : undefined }),
  component: FineScreen,
})

// S5c: the money reports, and the night's check against Stripe.
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  validateSearch: (search: Record<string, unknown>): ReportsSearch =>
    only({
      tab: search.tab === 'open' ? 'open' : undefined,
      from: isIsoDate(search.from) ? search.from : undefined,
      to: isIsoDate(search.to) ? search.to : undefined,
      by: search.by === 'month' ? 'month' : undefined,
    }),
  component: ReportsScreen,
})

const reconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reconciliation',
  validateSearch: (search: Record<string, unknown>): ReconciliationSearch =>
    only({ date: isIsoDate(search.date) ? search.date : undefined }),
  component: ReconciliationScreen,
})

// S9 (ADM-1, Q-51): the staff accounts, the Owner's (users.manage); a new one; one person.
const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people',
  component: PeopleScreen,
})

const newPersonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/new',
  component: NewPersonScreen,
})

const personRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/$userId',
  component: PersonScreen,
})

const devicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/devices',
  component: DevicesScreen,
})

// S2: backups, the background service, undelivered messages, alerts.
const systemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/system',
  component: SystemScreen,
})

// S10: the printed fallback kit, prepared by the worker and printed by the counter.
const kitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/kit',
  component: FallbackKitScreen,
})

// OPS-1: the rental terms written and put in force by the Owner (Regole del contratto).
const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/terms',
  component: TermsScreen,
})

const setPinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pin',
  component: SetPinScreen,
})

// S9: "Cambia password", beside "Cambia PIN". The forced change after a password someone
// else chose has no route: Root shows it in place of the whole app (ForcedPasswordScreen).
const passwordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/password',
  component: ChangePasswordScreen,
})

const routeTree = rootRoute.addChildren([
  todayRoute,
  bookingsRoute,
  newBookingRoute,
  bookingRoute,
  checkoutRoute,
  returnRoute,
  paperRoute,
  timelineRoute,
  pricesRoute,
  vehiclesRoute,
  vehicleRoute,
  modelsRoute,
  customersRoute,
  customerRoute,
  maintenanceRoute,
  cashRoute,
  fiscalRoute,
  finesRoute,
  fineRoute,
  reportsRoute,
  reconciliationRoute,
  peopleRoute,
  newPersonRoute,
  personRoute,
  devicesRoute,
  termsRoute,
  kitRoute,
  systemRoute,
  setPinRoute,
  passwordRoute,
])

/**
 * Holds only while every route keeps its own types. Inline in addChildren, each path
 * widened to a string, so every id did, and each screen's search read as `any`: a
 * route id that does not exist compiled, and so did any misuse of a search (UI-2e).
 */
type Holds<T extends true> = T
export type RouteIdsAreTyped = Holds<
  '/no-such-route' extends RouteIds<typeof routeTree> ? false : true
>

/**
 * S2: a screen that throws shows CrashScreen inside the frame, and is
 * reported as a crash (handled: false) under its route's id, never its URL.
 */
export const router = createRouter({
  routeTree,
  scrollRestoration: true,
  defaultErrorComponent: CrashScreen,
  defaultOnCatch: (error) => {
    reportError(error, {
      mechanism: 'router.error-boundary',
      handled: false,
      route: router.state.matches.at(-1)?.routeId ?? 'unknown',
    })
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
